import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { PageHeader, EmptyState, StatusBadge, StarRating } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const Btn = (p) => <Button {...p} className={`bg-[#E08A1E] hover:bg-[#C87515] text-white rounded-full ${p.className || ""}`} />;

const CycleDialog = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ name: "", start_date: "", end_date: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/review-cycles", f); toast.success("Review cycle created"); onSaved(); onOpenChange(false); } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-white" data-testid="cycle-dialog">
      <DialogHeader><DialogTitle>New review cycle</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5"><Label>Name</Label><Input required placeholder="e.g. H1 Appraisal" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cycle-name-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Start</Label><Input type="date" required value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="cycle-start-input" /></div>
          <div className="space-y-1.5"><Label>End</Label><Input type="date" required value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="cycle-end-input" /></div>
        </div>
        <Btn type="submit" className="w-full" data-testid="cycle-submit-button">Create</Btn>
      </form>
    </DialogContent></Dialog>
  );
};

const ReviewCreateDialog = ({ open, onOpenChange, cycleId, onSaved }) => {
  const [emps, setEmps] = useState([]);
  const [emp, setEmp] = useState("");
  const [goals, setGoals] = useState("");
  useEffect(() => {
    if (open) api.get("/employees").then((r) => setEmps(r.data.filter((e) => e.status === "active")));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    const list = goals.split("\n").map((g) => g.trim()).filter(Boolean).map((title) => ({ title }));
    try { await api.post("/reviews", { cycle_id: cycleId, employee_id: emp, goals: list }); toast.success("Review assigned"); setGoals(""); setEmp(""); onSaved(); onOpenChange(false); }
    catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-white" data-testid="review-create-dialog">
      <DialogHeader><DialogTitle>Assign review</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5"><Label>Employee</Label>
          <Select value={emp || undefined} onValueChange={setEmp}>
            <SelectTrigger data-testid="review-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Goals / KPIs (one per line)</Label><Textarea rows={5} required value={goals} onChange={(e) => setGoals(e.target.value)} data-testid="review-goals-input" /></div>
        <Btn type="submit" className="w-full" disabled={!emp} data-testid="review-create-submit">Assign</Btn>
      </form>
    </DialogContent></Dialog>
  );
};

const ReviewDialog = ({ review, mode, onClose, onSaved }) => {
  const [ratings, setRatings] = useState([]);
  const [comments, setComments] = useState("");
  useEffect(() => {
    if (review) { setRatings(review.goals.map(() => 0)); setComments(""); }
  }, [review]);
  if (!review) return null;
  const submit = async () => {
    try { await api.put(`/reviews/${review.id}/${mode}`, { ratings, comments }); toast.success("Review submitted"); onSaved(); onClose(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
      <DialogHeader><DialogTitle>{review.employee_name} · {review.cycle_name}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        {review.goals.map((g, i) => (
          <div key={i} className="rounded-xl border border-[#E5E5EA] p-3">
            <div className="font-semibold text-sm">{g.title}</div>
            <div className="flex flex-wrap gap-6 mt-2 text-xs text-[#6E6E73]">
              <div>Self <StarRating value={mode === "self" ? ratings[i] : g.self_rating} onChange={mode === "self" ? (v) => setRatings(ratings.map((r, j) => (j === i ? v : r))) : undefined} testid={`self-rating-${i}`} /></div>
              {mode !== "self" && <div>Manager <StarRating value={mode === "manager" ? ratings[i] : g.manager_rating} onChange={mode === "manager" ? (v) => setRatings(ratings.map((r, j) => (j === i ? v : r))) : undefined} testid={`manager-rating-${i}`} /></div>}
            </div>
          </div>
        ))}
        {review.self_comments && <div className="text-sm"><span className="font-semibold">Self comments:</span> {review.self_comments}</div>}
        {review.manager_comments && <div className="text-sm"><span className="font-semibold">Manager feedback:</span> {review.manager_comments}</div>}
        {review.overall_rating && <div className="text-sm font-bold" data-testid="review-overall">Overall rating: {review.overall_rating} / 5</div>}
        {mode !== "view" && <>
          <Textarea placeholder={mode === "self" ? "Your self-assessment" : "Manager feedback"} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="review-comments-input" />
          <Btn className="w-full" onClick={submit} disabled={ratings.some((r) => !r)} data-testid="review-submit-button">Submit</Btn>
        </>}
      </div>
    </DialogContent></Dialog>
  );
};

export default function Performance() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [cycles, setCycles] = useState([]);
  const [cycle, setCycle] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [open, setOpen] = useState(null);

  const loadCycles = () => api.get("/review-cycles").then((r) => setCycles(r.data));
  const loadReviews = () => api.get("/reviews", { params: cycle === "all" ? {} : { cycle_id: cycle } }).then((r) => setReviews(r.data));
  useEffect(() => {
    loadCycles();
  }, []);
  useEffect(() => {
    loadReviews();
  }, [cycle]); // eslint-disable-line react-hooks/exhaustive-deps

  const modeFor = (r) => (!isAdmin && r.status === "pending_self" ? "self" : isAdmin && r.status === "pending_manager" ? "manager" : "view");

  return (
    <div data-testid="performance-page">
      <PageHeader eyebrow="Growth" title="Performance reviews" subtitle="Employees self-assess each goal, then HR gives the final rating."
        actions={isAdmin && <>
          <Button variant="outline" className="rounded-full bg-white" onClick={() => setDlg("cycle")} data-testid="add-cycle-button"><Plus className="h-4 w-4 mr-1" />Cycle</Button>
          <Btn onClick={() => setDlg("review")} disabled={cycle === "all"} data-testid="assign-review-button"><Plus className="h-4 w-4 mr-1" />Assign review</Btn>
        </>} />
      <div className="mb-4">
        <Select value={cycle} onValueChange={setCycle}>
          <SelectTrigger className="w-72 bg-white" data-testid="cycle-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All cycles</SelectItem>{cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({fmtDate(c.start_date)} – {fmtDate(c.end_date)})</SelectItem>)}</SelectContent>
        </Select>
        {isAdmin && cycle === "all" && <p className="text-xs text-[#6E6E73] mt-2">Select a cycle to assign reviews.</p>}
      </div>
      {!reviews.length ? <EmptyState text="No reviews yet" testid="reviews-empty" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-[#E5E5EA] rounded-2xl p-5" data-testid={`review-card-${r.id}`}>
              <div className="flex justify-between gap-2"><div><div className="font-bold">{r.employee_name}</div><div className="text-xs text-[#6E6E73]">{r.employee_code} · {r.cycle_name}</div></div><StatusBadge status={r.status} /></div>
              <div className="text-sm text-[#6E6E73] mt-3">{r.goals.length} goal(s)</div>
              {r.overall_rating && <div className="mt-2"><StarRating value={Math.round(r.overall_rating)} testid={`overall-${r.id}`} /></div>}
              <Button size="sm" variant={modeFor(r) === "view" ? "outline" : "default"} className={`mt-4 ${modeFor(r) !== "view" ? "bg-[#0A0807] text-white" : ""}`}
                onClick={() => setOpen(r)} data-testid={`open-review-${r.id}`}>{modeFor(r) === "self" ? "Self-assess" : modeFor(r) === "manager" ? "Give rating" : "View"}</Button>
            </div>
          ))}
        </div>
      )}
      <CycleDialog open={dlg === "cycle"} onOpenChange={(o) => !o && setDlg(null)} onSaved={loadCycles} />
      <ReviewCreateDialog open={dlg === "review"} onOpenChange={(o) => !o && setDlg(null)} cycleId={cycle} onSaved={loadReviews} />
      <ReviewDialog review={open} mode={open ? modeFor(open) : "view"} onClose={() => setOpen(null)} onSaved={loadReviews} />
    </div>
  );
}
